import React, { useState, useEffect, useRef } from 'react';
import { Habit } from '../../services/habitService';
import './CompletionValueModal.css';

interface CompletionValueModalProps {
  habit: Habit;
  date: string;
  currentValue?: number;
  currentMeasurement?: number;
  onSave: (value: number | undefined, measurement: number | undefined) => void;
  onClose: () => void;
}

const CompletionValueModal: React.FC<CompletionValueModalProps> = ({
  habit,
  date,
  currentValue,
  currentMeasurement,
  onSave,
  onClose
}) => {
  const [countValue, setCountValue] = useState<string>(currentValue?.toString() || '');
  const [measurementValue, setMeasurementValue] = useState<string>(currentMeasurement?.toString() || '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Focus the appropriate input when modal opens
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const handleSave = () => {
    const count = countValue.trim() ? parseFloat(countValue) : undefined;
    const measurement = measurementValue.trim() ? parseFloat(measurementValue) : undefined;
    
    // If both are empty, treat as uncompleted
    if (!count && !measurement) {
      onSave(undefined, undefined);
    } else {
      onSave(count, measurement);
    }
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const isCountTracking = habit.tracking_type === 'count';
  const isMeasurementTracking = habit.tracking_type === 'measurement';
  const isBothTracking = habit.tracking_type === 'both';

  return (
    <div className="completion-value-modal-overlay" onClick={onClose}>
      <div className="completion-value-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="completion-value-modal-header">
          <div className="completion-value-modal-title">
            <span className="completion-value-modal-icon">{habit.icon}</span>
            <div>
              <h3>{habit.name}</h3>
              <p className="completion-value-modal-date">{formatDate(date)}</p>
            </div>
          </div>
          <button className="completion-value-modal-close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="completion-value-modal-body">
          {isCountTracking && (
            <div className="completion-value-input-group">
              <label>
                Count
                {habit.target_count && (
                  <span className="completion-value-target">Target: {habit.target_count}</span>
                )}
              </label>
              <input
                ref={inputRef}
                type="number"
                value={countValue}
                onChange={(e) => setCountValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter count..."
                min="0"
                step="1"
              />
            </div>
          )}

          {isMeasurementTracking && (
            <div className="completion-value-input-group">
              <label>
                {habit.measurement_unit || 'Value'}
                {habit.target_measurement && (
                  <span className="completion-value-target">Target: {habit.target_measurement} {habit.measurement_unit || ''}</span>
                )}
              </label>
              <input
                ref={inputRef}
                type="number"
                value={measurementValue}
                onChange={(e) => setMeasurementValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Enter ${habit.measurement_unit || 'value'}...`}
                min="0"
                step={habit.measurement_unit === 'km' || habit.measurement_unit === 'miles' ? '0.1' : '1'}
              />
            </div>
          )}

          {isBothTracking && (
            <>
              <div className="completion-value-input-group">
                <label>
                  Count
                  {habit.target_count && (
                    <span className="completion-value-target">Target: {habit.target_count}</span>
                  )}
                </label>
                <input
                  ref={inputRef}
                  type="number"
                  value={countValue}
                  onChange={(e) => setCountValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter count..."
                  min="0"
                  step="1"
                />
              </div>
              <div className="completion-value-input-group">
                <label>
                  {habit.measurement_unit || 'Value'}
                  {habit.target_measurement && (
                    <span className="completion-value-target">Target: {habit.target_measurement} {habit.measurement_unit || ''}</span>
                  )}
                </label>
                <input
                  type="number"
                  value={measurementValue}
                  onChange={(e) => setMeasurementValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={`Enter ${habit.measurement_unit || 'value'}...`}
                  min="0"
                  step={habit.measurement_unit === 'km' || habit.measurement_unit === 'miles' ? '0.1' : '1'}
                />
              </div>
            </>
          )}
        </div>

        <div className="completion-value-modal-footer">
          <button className="completion-value-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="completion-value-save-btn" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompletionValueModal;

